import React from 'react';
import { Zap, CheckCircle2, Search, FileText, ClipboardList, Receipt, Layers, PenTool, BookOpen, AlertTriangle, Flag } from 'lucide-react';
type OrderBumpsProps = {
  bump1Checked: boolean;
  setBump1Checked: (val: boolean) => void;
  bump2Checked: boolean;
  setBump2Checked: (val: boolean) => void;
};
export function OrderBumps({
  bump1Checked,
  setBump1Checked,
  bump2Checked,
  setBump2Checked
}: OrderBumpsProps) {
  return <div className="space-y-6 mb-10" data-id="element-527">
      {/* ORDER BUMP #1 */}
      <div className={`relative border-2 border-dashed rounded-2xl p-6 md:p-8 transition-all order-bump-flash ${bump1Checked ? 'border-brand-gold bg-brand-cream/80' : 'border-gray-300 bg-white'}`} data-id="element-528">
        {/* Badge */}
        <div className="flex justify-center -mt-10 mb-4" data-id="element-529">
          <div className="inline-flex items-center gap-1.5 bg-brand-gold text-brand-dark font-bold text-xs uppercase tracking-wider px-4 py-1.5 rounded-full shadow-md" data-id="element-530">
            <Zap className="w-3.5 h-3.5" data-id="element-531" />
            ONE-TIME OFFER — JUST $9 TODAY
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-6" data-id="element-532">
          {/* Image */}
          <div className="flex-shrink-0 w-full md:w-48" data-id="element-533">
            <img src="https://cdn.magicpatterns.com/uploads/bQVWYTcZdEemZ2RAcqcXX9/Order_Bump_1_Image_The_Senior_Benefit.png" alt="The Hidden Wealth Retirement Audit" className="w-full h-auto rounded-xl shadow-lg" data-id="element-534" />
          </div>

          {/* Content */}
          <div className="flex-1" data-id="element-535">
            <h4 className="font-serif text-xl md:text-2xl font-bold text-brand-dark mb-2 leading-snug" data-id="element-536">
              "What if your biggest paycheck is already sitting in your name...
              unclaimed?"
            </h4>
            <p className="text-gray-700 text-base leading-relaxed mb-4" data-id="element-537">
              Most seniors focus only on their monthly expenses — and miss the
              large sources of accumulated wealth that legally belong to them
              and have been sitting dormant for decades. We are talking about
              pension credits from old jobs, forgotten insurance refunds,
              inactive bank accounts, or specific senior tax deductions that
              most accountants overlook every single year.
            </p>
            <p className="text-gray-700 text-base leading-relaxed mb-4" data-id="element-538">
              <strong className="text-brand-dark" data-id="element-539">
                The Hidden Wealth Retirement Audit
              </strong>{' '}
              is a step-by-step audit designed specifically for adults 60+ to
              systematically check every one of these overlooked wealth sources
              and find out, once and for all, if money is sitting in your name
              right now.
            </p>

            <p className="text-sm font-bold uppercase tracking-wider text-brand-gold mb-3" data-id="element-540">
              Inside this resource:
            </p>
            <div className="space-y-2 mb-4" data-id="element-541">
              <div className="flex items-start gap-2" data-id="element-542">
                <CheckCircle2 className="w-5 h-5 text-brand-gold flex-shrink-0 mt-0.5" data-id="element-543" />
                <p className="text-sm text-gray-700" data-id="element-544">
                  <strong className="text-brand-dark" data-id="element-545">
                    The 7-Source Hidden Wealth Checklist:
                  </strong>{' '}
                  A systematic review of unclaimed pensions, veteran
                  entitlements, and dormant assets.
                </p>
              </div>
              <div className="flex items-start gap-2" data-id="element-546">
                <CheckCircle2 className="w-5 h-5 text-brand-gold flex-shrink-0 mt-0.5" data-id="element-547" />
                <p className="text-sm text-gray-700" data-id="element-548">
                  <strong className="text-brand-dark" data-id="element-549">
                    The Unclaimed Property Database Guide:
                  </strong>{' '}
                  Exactly how to search national and state databases for money
                  already registered in your name.
                </p>
              </div>
              <div className="flex items-start gap-2" data-id="element-550">
                <CheckCircle2 className="w-5 h-5 text-brand-gold flex-shrink-0 mt-0.5" data-id="element-551" />
                <p className="text-sm text-gray-700" data-id="element-552">
                  <strong className="text-brand-dark" data-id="element-553">
                    The Pension Trace Worksheet:
                  </strong>{' '}
                  Ideal if you worked multiple jobs over a 40-year career and
                  never confirmed if you were paid everything owed to you.
                </p>
              </div>
              <div className="flex items-start gap-2" data-id="element-554">
                <CheckCircle2 className="w-5 h-5 text-brand-gold flex-shrink-0 mt-0.5" data-id="element-555" />
                <p className="text-sm text-gray-700" data-id="element-556">
                  <strong className="text-brand-dark" data-id="element-557">
                    The Senior Tax Deduction Audit:
                  </strong>{' '}
                  The high-value tax benefits your accountant probably isn't
                  telling you about.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-4" data-id="element-558">
              <span className="text-gray-500 text-base" data-id="element-559">Regular Price:</span>
              <span className="text-gray-400 line-through text-lg" data-id="element-560">$47.00</span>
              <span className="text-brand-dark font-bold text-base" data-id="element-561">
                Have it today for just
              </span>
              <span className="text-brand-gold font-black text-2xl" data-id="element-562">$9.00</span>
            </div>

            <p className="text-sm text-gray-600 italic mb-4" data-id="element-563">
              The Main Guide finds your monthly credits. The Hidden Wealth
              Retirement Audit finds the money most seniors never think to look
              for.
            </p>

            {/* Checkbox */}
            <label className="flex items-start gap-3 cursor-pointer group" data-id="element-564">
              <div className={`w-7 h-7 flex-shrink-0 rounded-md border-2 flex items-center justify-center transition-all mt-0.5 ${bump1Checked ? 'bg-brand-gold border-brand-gold' : 'border-gray-400 bg-white group-hover:border-brand-gold'}`} onClick={() => setBump1Checked(!bump1Checked)} data-id="element-565">
                {bump1Checked && <svg className="w-4 h-4 text-brand-dark" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3} data-id="element-566">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" data-id="element-567" />
                  </svg>}
              </div>
              <span className="text-base font-bold text-brand-dark leading-snug" data-id="element-568">
                YES — Add "The Hidden Wealth Retirement Audit" to my order for
                just $9.
              </span>
            </label>
          </div>
        </div>
      </div>

      {/* ORDER BUMP #2 */}
      <div className={`relative border-2 border-dashed rounded-2xl p-6 md:p-8 transition-all order-bump-flash ${bump2Checked ? 'border-brand-gold bg-brand-cream/80' : 'border-gray-300 bg-white'}`} data-id="element-569">
        {/* Badge */}
        <div className="flex justify-center -mt-10 mb-4" data-id="element-570">
          <div className="inline-flex items-center gap-1.5 bg-brand-gold text-brand-dark font-bold text-xs uppercase tracking-wider px-4 py-1.5 rounded-full shadow-md" data-id="element-571">
            <Zap className="w-3.5 h-3.5" data-id="element-572" />
            ONE-TIME OFFER — JUST $9 TODAY
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-6" data-id="element-573">
          {/* Image */}
          <div className="flex-shrink-0 w-full md:w-48" data-id="element-574">
            <img src="https://cdn.magicpatterns.com/uploads/thYDoEocWVeeYpqXbTozs6/Order_Bump_2_Image_The_Senior_Benefit.png" alt="The Priority Advocacy Master System" className="w-full h-auto rounded-xl shadow-lg" data-id="element-575" />
          </div>

          {/* Content */}
          <div className="flex-1" data-id="element-576">
            <h4 className="font-serif text-xl md:text-2xl font-bold text-brand-dark mb-2 leading-snug" data-id="element-577">
              "What happens if they say NO? Here is exactly what to do next."
            </h4>
            <p className="text-gray-700 text-base leading-relaxed mb-4" data-id="element-578">
              You followed the steps. You filled out the application. You
              submitted every document. And yet... they denied you. Or worse,
              they stopped responding entirely. Most people accept a "No" as the
              final answer. They are wrong.
            </p>
            <p className="text-gray-700 text-base leading-relaxed mb-4" data-id="element-579">
              There is a formal system of advocacy and escalation — made up of
              real government offices with real authority — that exists
              specifically to overturn wrongful denials and force agencies to
              respond. Almost nobody uses this system because almost nobody
              knows it exists.
            </p>
            <p className="text-gray-700 text-base leading-relaxed mb-4" data-id="element-580">
              <strong className="text-brand-dark" data-id="element-581">
                The Priority Advocacy Master System
              </strong>{' '}
              is your strategic playbook for moving through every level of this
              system — in the correct order — without needing a lawyer and
              without spending a single dollar.
            </p>

            <p className="text-sm font-bold uppercase tracking-wider text-brand-gold mb-3" data-id="element-582">
              Inside this resource:
            </p>
            <div className="space-y-2 mb-4" data-id="element-583">
              <div className="flex items-start gap-2" data-id="element-584">
                <CheckCircle2 className="w-5 h-5 text-brand-gold flex-shrink-0 mt-0.5" data-id="element-585" />
                <p className="text-sm text-gray-700" data-id="element-586">
                  <strong className="text-brand-dark" data-id="element-587">
                    The 5-Level Escalation Ladder:
                  </strong>{' '}
                  A clear path from your first denial all the way to
                  Congressional constituent services.
                </p>
              </div>
              <div className="flex items-start gap-2" data-id="element-588">
                <CheckCircle2 className="w-5 h-5 text-brand-gold flex-shrink-0 mt-0.5" data-id="element-589" />
                <p className="text-sm text-gray-700" data-id="element-590">
                  <strong className="text-brand-dark" data-id="element-591">
                    The Formal Appeal Script Library:
                  </strong>{' '}
                  Word-for-word scripts for writing appeal letters and making
                  effective escalation calls.
                </p>
              </div>
              <div className="flex items-start gap-2" data-id="element-592">
                <CheckCircle2 className="w-5 h-5 text-brand-gold flex-shrink-0 mt-0.5" data-id="element-593" />
                <p className="text-sm text-gray-700" data-id="element-594">
                  <strong className="text-brand-dark" data-id="element-595">
                    The Senior Advocacy Office Directory:
                  </strong>{' '}
                  Key contacts for ombudsmen, SHIP Medicare counselors, and free
                  legal aid for seniors.
                </p>
              </div>
              <div className="flex items-start gap-2" data-id="element-596">
                <CheckCircle2 className="w-5 h-5 text-brand-gold flex-shrink-0 mt-0.5" data-id="element-597" />
                <p className="text-sm text-gray-700" data-id="element-598">
                  <strong className="text-brand-dark" data-id="element-599">
                    The "They Said No" Response Protocol:
                  </strong>{' '}
                  How to identify whether your denial was a program error or a
                  documentation issue — and how to correct it.
                </p>
              </div>
              <div className="flex items-start gap-2" data-id="element-600">
                <CheckCircle2 className="w-5 h-5 text-brand-gold flex-shrink-0 mt-0.5" data-id="element-601" />
                <p className="text-sm text-gray-700" data-id="element-602">
                  <strong className="text-brand-dark" data-id="element-603">
                    The Congressional Services Script:
                  </strong>{' '}
                  How to contact your local representatives so their staff can
                  help you navigate federal agency problems.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-4" data-id="element-604">
              <span className="text-gray-500 text-base" data-id="element-605">Regular Price:</span>
              <span className="text-gray-400 line-through text-lg" data-id="element-606">$47.00</span>
              <span className="text-brand-dark font-bold text-base" data-id="element-607">
                Have it today for just
              </span>
              <span className="text-brand-gold font-black text-2xl" data-id="element-608">$9.00</span>
            </div>

            <p className="text-sm text-gray-600 italic mb-4" data-id="element-609">
              The main guide gets you to the application. The Priority Advocacy
              Master System ensures that if they say "no," you know exactly how
              to fight back and win.
            </p>

            {/* Checkbox */}
            <label className="flex items-start gap-3 cursor-pointer group" data-id="element-610">
              <div className={`w-7 h-7 flex-shrink-0 rounded-md border-2 flex items-center justify-center transition-all mt-0.5 ${bump2Checked ? 'bg-brand-gold border-brand-gold' : 'border-gray-400 bg-white group-hover:border-brand-gold'}`} onClick={() => setBump2Checked(!bump2Checked)} data-id="element-611">
                {bump2Checked && <svg className="w-4 h-4 text-brand-dark" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3} data-id="element-612">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" data-id="element-613" />
                  </svg>}
              </div>
              <span className="text-base font-bold text-brand-dark leading-snug" data-id="element-614">
                YES — Add "The Priority Advocacy Master System" to my order for
                just $9.
              </span>
            </label>
          </div>
        </div>
      </div>
    </div>;
}